import { BObject } from './Objects/BObject';
import { BezieApp } from './BezieApp';
import { Point, NumPair, NumberUtils } from './Misc/Utils';
import { UIUpdate } from "./UI/UIUpdate";
import { InputHandler, InputListener, KeyInputListener } from './Input/InputHandler';
import { AssistiveObjectsHandler, AssistiveObjectsHolder } from './Objects/AssistiveObjects/AssistiveObjectsHandler';
import { ObjSelectionHolder } from './Objects/ObjSelectionHolder';
import { BObjectProvider } from './Objects/Providers/BObjectProvider';
import { BObjectManager } from './Objects/BObjectManager';

export class Scene implements InputListener, KeyInputListener{
    private sceneObjects : BObjectProvider
    private sceneObjectManager: BObjectManager
    private selectedObjs : ObjSelectionHolder
    private inputHandler: InputHandler
    private assistiveObjectsHandler: AssistiveObjectsHandler

    private objCount : number = 0
    
    private totalFrames : number = 24*8
    private curFrame : number = 0 // range: 1 to totalFrames
    public isRendering: boolean = false

    public isUserClicking: boolean = false

    constructor(inputHandler: InputHandler){
        this.sceneObjects = new BObjectProvider();
        this.inputHandler = inputHandler;
        this.inputHandler.addListener(this);
        this.inputHandler.addKeyListener(this);
        this.assistiveObjectsHandler = new AssistiveObjectsHandler();
        this.selectedObjs = new ObjSelectionHolder(this.inputHandler, this.assistiveObjectsHandler, this.sceneObjects);
        this.sceneObjectManager = new BObjectManager(this.sceneObjects, this.selectedObjs); //pass by reference
        console.log("initialised scene");
    }
    
    addBObject(object : BObject) : void{
        this.objCount = this.objCount + 1;
        object.setId(this.getNewId());
        this.sceneObjects.add(object); 
        console.log("added object "+this.objCount);
    }

    /**
     * Method to request scene to STOP hearing for input events. 
     * Called by BVectorBuilder
     * So that clicks/mouse move doest not select objects.
     */
    onObjectCreationStart():void{
        this.inputHandler.removeListener(this);
        this.inputHandler.removeKeyListener(this);
    }

    /**
     * Method to request scene to START hearing for input events. 
     * Called by BVectorBuilder
     * So that clicks/mouse move can start selecting objects for editing.
     */
    onObjectCreationFinish():void{
        this.inputHandler.addListener(this);
        this.inputHandler.addKeyListener(this);
    }

    draw(canvasCtx : CanvasRenderingContext2D, playStatus : number) : void{
        //When playing the scene, update the frame number
        if(playStatus == BezieApp.PLAY_STATUS_PLAY){
            this.updateFrameNumber();
        }

        //Inform Objects to update for current frame
        this.sceneObjectManager.onFrameUpdate(this.curFrame);

        //Request objects to draw
        this.sceneObjectManager.requestDraw(canvasCtx, playStatus, this.curFrame);

        //Request assistive objects to draw
        this.assistiveObjectsHandler.updateAssistiveObjects(canvasCtx, playStatus);
    }

    onMouseDown(point: Point): void {
        this.isUserClicking = true;
        this.selectObjectForEditing(point);
    }
    onMouseUp(point: Point): void {
        this.isUserClicking = false;
    }
    onDoubleClick(point: Point): void {
        this.selectedObjs.editStructure(true);
    }
    onMouseMove(from: Point, to: Point): void {
        if(this.isUserClicking){
            this.editSelectedObject(from, to);
        }
    }
    onKeyDown(key: string): void {
        if(key == 'Escape'){
            //deselection
            console.log("Escape pressed");
            this.resetEditStructureState();
            this.deselectObject();
        }else if(key == 'Shift'){
            //multi selection on
            console.log("Shift pressed");
            this.selectedObjs.setMultiSelectOn(true);
        }
    }

    onKeyUp(key: string): void {
        if(key == 'Shift'){
            //multi selection off
            console.log("Shift unpressed");
            this.selectedObjs.setMultiSelectOn(false);
        }
    }

    drawSelObjectOutline(outlineCanvasCtx : CanvasRenderingContext2D, playStatus : number): void{
        if(playStatus != BezieApp.PLAY_STATUS_PLAY){
            this.selectedObjs.drawControlPoints(outlineCanvasCtx, this.curFrame);
        }
    }

    resetObjectPos() : void{
        this.setCurFrame(0);
        this.sceneObjectManager.onFrameUpdate(this.curFrame);
    }

    resetEditStructureState(): void{
        if(!this.selectedObjs.isEmpty()){
            this.selectedObjs.editStructure(false); //if edit structure was called earlier, this resets.
        }
    }

    selectObjectForEditing(point : Point) : void{
        if(!this.selectedObjs.isEmpty() && this.selectedObjs.checkIfAlreadyInSelection(point)){
            console.log("selecting same object")    
            //dont do anything. selected obj is being clicked on again (minor optimziation)
        }else{
            this.resetEditStructureState();
            
            var selectedNow = false;
            for(let object of this.sceneObjects.getObjects()){
                if(object.checkSelection(point)){
                    this.selectedObjs.addToSelection(object);
                    selectedNow = true;
                    break; //so that object on top is selected
                }
            }

            if(!selectedNow){
                this.selectedObjs.clearSelection();
            }

            UIUpdate.notifySelectionChangeListeners(this.selectedObjs.getLastAddedObj(), this.selectedObjs.holdsMultipleSelectedObjs());
        }
    }

    deselectObject(){
        this.selectedObjs.clearSelection();
        UIUpdate.notifySelectionChangeListeners(this.selectedObjs.getLastAddedObj(), this.selectedObjs.holdsMultipleSelectedObjs());
    }

    selectObjectById(id: number): void{
        var selObj = this.getObjectById(id);
        if(selObj){
            this.selectedObjs.addToSelection(selObj);
        }
    }

    editSelectedObject(lastMousePoint : Point, curMousePoint : Point) : void{
        if(!this.selectedObjs.isEmpty()){
            this.selectedObjs.edit(lastMousePoint, curMousePoint);
            UIUpdate.updatePropertiesUI(this.selectedObjs.getLastAddedObj());
        }
    }

    //Returns the selected object. At the moment returns the last added object
    getSelectedObject() : BObject | null{
        return this.selectedObjs.getLastAddedObj();
    }

    //Method to get all selected objects. Used by none at the moment.
    getAllSelectedObjects(): Array<BObject>{
        return this.selectedObjs.getAllObjects();
    }

    //Deletes the selected object(s). If there are multiple selected objects, loops through them and deletes
    deleteSelectedObject() : void{
        this.sceneObjectManager.deleteSelectedObject();
        UIUpdate.notifySelectionChangeListeners(this.selectedObjs.getLastAddedObj(), this.selectedObjs.holdsMultipleSelectedObjs());
    }

    moveSelectedObjectByALayer(forward : boolean) : void{
        this.sceneObjectManager.moveSelectedObjectByALayer(forward);
    }

    getObjectById(id: number): BObject|null{
        if(Number(id)!=NaN){
            return this.sceneObjects.getObjectById(id);
        }
        return null;
    }

    /**
     * Called by layers Side menu ui. Only one object needs to be deleted in this case
     * @param id of the object to be deleted
     */
    deleteObjectById(id: number) : void{
        if(Number(id)!=NaN){
            this.sceneObjectManager.deleteObjectById(id);
            UIUpdate.notifySelectionChangeListeners(this.selectedObjs.getLastAddedObj(), this.selectedObjs.holdsMultipleSelectedObjs());
        }
    }

    moveObjectALayerById(forward : boolean, id: number) : void{
        if(Number(id)!=NaN){
            this.sceneObjectManager.moveObjectByALayer(forward, id);
        }
    }

    private getNewId() : number{
        return this.objCount;
    }

    private updateFrameNumber() : void{
        console.log("updating frame number")
        this.curFrame +=1;
        if(this.curFrame >= this.totalFrames){
            this.curFrame = 0;
        }
        if(!this.isRendering){
            UIUpdate.updatePlaybackUI(this.curFrame);
        }
    }

    public prepareToPlay() : void{
        this.curFrame = 0;
        this.sceneObjectManager.requestResetAnimationPos();
    }

    public setCurFrame(value : number){
        if(value >=0 && value < this.totalFrames){
            this.curFrame = value;
            UIUpdate.updatePlaybackUI(this.curFrame);
        }
    }

    public getCurFrame() : number {
        return this.curFrame;
    }

    public getTotalFrames() : number{
        return this.totalFrames;
    }

    
    public setTotalFrames(tFrames: number): void{
        if(Number(tFrames)!= NaN && tFrames > 1){
            this.totalFrames = Math.floor(tFrames);
        
            for(var obj of this.sceneObjects.getObjects()){
                obj.updateFrameRange(this.getTotalFrames());
            }
            
            UIUpdate.updatePropertiesUI(this.getSelectedObject());
            UIUpdate.notifyTotalFramesChange(this.totalFrames);
        }
        console.log("tf rec:"+tFrames+" set:"+this.totalFrames);
    }

    public getFrameRange() : NumPair{
        return [0, this.totalFrames-1];
    }

    public duplicateSelectedObject(){
        if(!this.selectedObjs.isEmpty()){
            var clones = this.selectedObjs.cloneSelected();
            this.selectedObjs.clearSelection();
            this.selectedObjs.setMultiSelectOn(true);
            for(var clone of clones){
                this.addBObject(clone);
                this.selectedObjs.addToSelection(clone);
                clone.moveBy([40,40]); // moves 40px right.
            }
            this.selectedObjs.setMultiSelectOn(false);
            UIUpdate.notifySelectionChangeListeners(this.selectedObjs.getLastAddedObj(), this.selectedObjs.holdsMultipleSelectedObjs());
        }
    }

    public getFirstObject() : BObject | null{
        if(this.sceneObjects.getCount() >= 1){
            return this.sceneObjects.getObject(0);
        }
        return null;
    }

    public getAssistiveObjectsHolder(): AssistiveObjectsHolder{
        return this.assistiveObjectsHandler;
    }

    public getAllObjects(): Array<BObject>{
        return this.sceneObjects.getObjects();
    }
}